/**
 * Groomer operational surface (#290).
 *
 * Grooming queue for today's appointments:
 *  - Sub-service + add-ons display
 *  - Start / complete actions with notes
 *  - Grooming notes inline editor
 *
 * Server gates: bookings.grooming:read:self / bookings.grooming:write:self.
 */
import { useState, useEffect, useCallback } from 'react';
import type { ClientConfig } from '@ferro/config';
import { PageHeader } from '@ferro/ui';
import { useAuth } from '../contexts/AuthContext.js';
import { groomerQueueApi, todayIsoDate, humanizeApiError, type GroomerQueueItem } from '../lib/api.js';
import { TimeClockCard, type TimeClockStatus } from '../components/timeclock/TimeClockCard.js';
import { WelcomeCard } from '../components/WelcomeCard.js';

type GroomerHomeProps = { config: ClientConfig };

function statusLabel(status: GroomerQueueItem['status']): string {
  const map: Record<GroomerQueueItem['status'], string> = {
    pending: 'Pending',
    confirmed: 'Confirmed',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return map[status] ?? status;
}

function statusPillClass(status: GroomerQueueItem['status']): string {
  const base = 'ferro-status-pill';
  switch (status) {
    case 'completed': return `${base} ferro-status-pill--success`;
    case 'in_progress': return `${base} ferro-status-pill--pending`;
    case 'cancelled': return `${base} ferro-status-pill--danger`;
    default: return `${base} ferro-status-pill--neutral`;
  }
}

function cents(n: number): string {
  return `$${(n / 100).toFixed(2)}`;
}

export function GroomerHome({ config }: GroomerHomeProps): JSX.Element {
  const { staff } = useAuth();
  const apiUrl = config.deployment.apiUrl;
  const [date, setDate] = useState(todayIsoDate);
  const [queue, setQueue] = useState<GroomerQueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  // #427 W1a — punch status lifted from the Time Clock card (spec W1a /
  // P2): clocked-out ⇒ Clock In is the view's sole filled-primary and
  // the queue's Start/Complete actions demote to secondary.
  const [clockStatus, setClockStatus] = useState<TimeClockStatus>('unknown');

  const load = useCallback(async () => {
    setIsLoading(true); setError(null);
    const res = await groomerQueueApi.getQueue(apiUrl, date);
    setIsLoading(false);
    if (res.error) setError(humanizeApiError(res.error));
    else if (res.data) setQueue(res.data.queue);
  }, [apiUrl, date]);

  useEffect(() => { void load(); }, [load]);

  const handleStart = async (item: GroomerQueueItem): Promise<void> => {
    setPendingId(item.id); setActionError(null);
    const res = await groomerQueueApi.startGrooming(apiUrl, item.id);
    setPendingId(null);
    if (res.error) { setActionError(res.error); return; }
    if (res.data) setQueue((prev) => prev.map((x) => x.id === item.id ? res.data!.item : x));
  };

  const handleComplete = async (item: GroomerQueueItem): Promise<void> => {
    const n = notes[item.id] ?? '';
    setPendingId(item.id); setActionError(null);
    const res = await groomerQueueApi.completeGrooming(apiUrl, item.id, n);
    setPendingId(null);
    if (res.error) { setActionError(res.error); return; }
    if (res.data) {
      setQueue((prev) => prev.map((x) => x.id === item.id ? res.data!.item : x));
      setNotes((prev) => { const n2 = { ...prev }; delete n2[item.id]; return n2; });
    }
  };

  const handleSaveNotes = async (item: GroomerQueueItem): Promise<void> => {
    const n = notes[item.id] ?? '';
    if (!n.trim()) return;
    setPendingId(`notes-${item.id}`); setActionError(null);
    const res = await groomerQueueApi.updateNotes(apiUrl, item.id, n);
    setPendingId(null);
    if (res.error) setActionError(res.error);
    else if (res.data) setQueue((prev) => prev.map((x) => x.id === item.id ? res.data!.item : x));
  };

  const prev = (): void => {
    const d = new Date(date); d.setDate(d.getDate() - 1);
    setDate(d.toISOString().slice(0, 10));
  };
  const next = (): void => {
    const d = new Date(date); d.setDate(d.getDate() + 1);
    setDate(d.toISOString().slice(0, 10));
  };

  const pending = queue.filter((i) => i.status !== 'completed' && i.status !== 'cancelled').length;
  const done = queue.filter((i) => i.status === 'completed').length;

  // #427 W1a view-level primary hierarchy (P2 — one filled-primary):
  // while clocked out the card owns the primary and queue actions render
  // via the deprioritized modifier; once clocked in the queue resumes
  // primary and the card (primaryDemoted) renders secondary.
  const queuePrimaryClass =
    clockStatus === 'clocked_out'
      ? 'ferro-btn ferro-btn--deprioritized ferro-btn--sm'
      : 'ferro-btn ferro-btn--primary ferro-btn--sm';

  return (
    <section className="ferro-admin-page ferro-role-screen">
      <PageHeader
        title={`Grooming Queue${staff?.first_name ? ` · ${staff.first_name}` : ''}`}
        subtitle={`${queue.length} appointment${queue.length !== 1 ? 's' : ''}`}
      />

      {/* #431 — first login only: the groomer blurb from their invite
          email, echoed on the screen the email pointed at. */}
      <WelcomeCard config={config} />

      {/* #427 W1a — punch in from the phone right on arrival (story S1,
          P7 hands-first). */}
      <TimeClockCard
        config={config}
        primaryDemoted={clockStatus !== 'clocked_out'}
        onStatusChange={setClockStatus}
      />

      {/* Date nav */}
      <div className="ferro-date-nav">
        <button type="button" className="ferro-btn ferro-btn--outline ferro-btn--sm" onClick={prev} aria-label="Previous day">‹</button>
        <span className="ferro-date-nav__label">{new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
        <button type="button" className="ferro-btn ferro-btn--outline ferro-btn--sm" onClick={next} aria-label="Next day">›</button>
        <button type="button" className="ferro-btn ferro-btn--ghost ferro-btn--sm" onClick={() => setDate(todayIsoDate())}>Today</button>
      </div>

      {!isLoading && !error && (
        <div className="ferro-summary-strip">
          <span className="ferro-summary-chip">{pending} remaining</span>
          <span className="ferro-summary-chip ferro-summary-chip--accent">{done} completed</span>
        </div>
      )}

      {actionError && (
        <div className="ferro-alert ferro-alert--error ferro-alert--dismissible" role="alert">
          <span>{actionError}</span>
          <button type="button" className="ferro-alert__dismiss" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {isLoading && (
        <div className="ferro-skeleton-stack" data-testid="groomer-skeleton">
          {[1, 2, 3].map((i) => <div key={i} className="ferro-skeleton ferro-skeleton--stat" />)}
        </div>
      )}

      {error && !isLoading && (
        <div className="ferro-alert ferro-alert--error" role="alert">
          {error}
          <button type="button" className="ferro-btn ferro-btn--sm ferro-btn--outline" style={{ marginLeft: '12px' }} onClick={() => void load()}>Retry</button>
        </div>
      )}

      {!isLoading && !error && queue.length === 0 && (
        <div className="ferro-empty-card" data-testid="groomer-empty">
          <div className="ferro-empty-card__icon">✂️</div>
          <p>No grooming appointments for this day.</p>
        </div>
      )}

      <div className="ferro-roster" data-testid="groomer-queue">
        {queue.map((item) => {
          const isInProgress = item.status === 'in_progress';
          const isDone = item.status === 'completed';
          const isCancelled = item.status === 'cancelled';
          return (
            <div
              key={item.id}
              className={`ferro-roster-card${isInProgress ? ' ferro-roster-card--inprogress' : ''}${isDone ? ' ferro-roster-card--completed' : ''}`}
              data-testid={`groomer-card-${item.id}`}
            >
              <div className="ferro-roster-card__header">
                <div>
                  <h3 className="ferro-roster-card__name">{item.dogName}</h3>
                  <p className="ferro-roster-card__meta">
                    {item.breed ?? 'Dog'}{item.sizeCategory ? ` · ${item.sizeCategory}` : ''} · {item.customerFirstName} {item.customerLastName}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                  <span className={statusPillClass(item.status)}>{statusLabel(item.status)}</span>
                  <span className="ferro-mono" style={{ fontSize: '0.88rem' }}>{cents(item.totalCents)}</span>
                </div>
              </div>

              {item.subServiceDisplay && (
                <p className="ferro-roster-card__meta">
                  Service: <strong>{item.subServiceDisplay}</strong>
                  {item.addOns.length > 0 && ` + ${item.addOns.join(', ')}`}
                </p>
              )}

              {item.scheduledTime && (
                <p className="ferro-roster-card__meta">
                  Time: {new Date(`1970-01-01T${item.scheduledTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </p>
              )}

              {item.groomingNotes && (
                <p className="ferro-roster-card__meta" style={{ fontStyle: 'italic' }}>{item.groomingNotes}</p>
              )}

              {/* Notes editor */}
              {!isCancelled && (
                <>
                  <textarea
                    className="ferro-notes-area"
                    placeholder="Grooming notes…"
                    value={notes[item.id] ?? item.groomingNotes ?? ''}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [item.id]: e.target.value }))}
                    aria-label={`Notes for ${item.dogName}`}
                  />
                  <div className="ferro-roster-card__actions">
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost ferro-btn--sm"
                      disabled={!notes[item.id]?.trim() || pendingId === `notes-${item.id}`}
                      onClick={() => void handleSaveNotes(item)}
                    >
                      {pendingId === `notes-${item.id}` ? '…' : 'Save Notes'}
                    </button>
                    {(item.status === 'pending' || item.status === 'confirmed') && (
                      <button
                        type="button"
                        className={queuePrimaryClass}
                        disabled={pendingId === item.id}
                        onClick={() => void handleStart(item)}
                      >
                        {pendingId === item.id ? '…' : 'Start Groom'}
                      </button>
                    )}
                    {isInProgress && (
                      <button
                        type="button"
                        className={queuePrimaryClass}
                        disabled={pendingId === item.id}
                        onClick={() => void handleComplete(item)}
                      >
                        {pendingId === item.id ? '…' : 'Complete'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
